define((require) => {
    "use strict";

    const _                = require("underscore");
    const Marionette       = require("marionette");
    const ImageWidgetModel = require("shared/ui/experience/widgets/imageWidget/ImageWidgetModel");
    const Promise          = require("shared/util/Promise");
    const Rectangle        = require("shared/util/Rectangle");
    const Class            = require("shared/util/Class");

    function getWidgetRectFromModel(widget, experience) {
        const position = experience.getPixelPosition(widget.pick(["top", "left"]));
        const size = experience.getPixelSize(widget.pick(["width", "height"]));

        return new Rectangle(_.extend(position, size));
    }

    const ImageWidgetProcessStrategy = Class.extend("ImageWidgetProcessStrategy", {

        /**
         * The current experience.
         *
         * @type {ExperienceModel}
         */
        _experience: null,

        /**
         * The initial viewport.
         *
         * @type {Rectangle}
         */
        _viewportVisibility: null,

        /**
         * Convenience map.
         *
         * @type {Map.<ImageWidgetModel, SceneViewer>}
         */
        _imageToSceneMap: null,

        constructor(experienceViewModel) {
            this._experience = experienceViewModel.get("experience");
            this._viewportVisibility = experienceViewModel.get("viewportVisibility");
            this._imageToSceneMap = new Map();
        },

        execute(initialScenes) {
            const sceneViewers = _.isArray(initialScenes) ? initialScenes : [initialScenes];
            const images = [];

            // Collect all images (widgets) from the specified initial scenes.
            // All other widgets are immediately added to their respective scene viewers.
            sceneViewers.forEach((scene) => {
                scene.model.get("widgets").each((widget) => {
                    if (widget instanceof ImageWidgetModel) {
                        // Store the images widgets...
                        images.push(widget);
                        this._imageToSceneMap.set(widget, scene);
                    } else {
                        // This will render "other" widgets immediately...
                        scene.collection.add(widget);
                    }
                });
            });


            let biggestIntersectionArea = 0;

            const imagesInViewport = [];
            const viewportVisibilityArea = this._viewportVisibility.area();

            let heroImage;
            let i = images.length;

            // Iterate the collected images...
            while (i--) {
                const imageWidget = images[i];
                const widgetRectangle = getWidgetRectFromModel(imageWidget, this._experience);
                const widgetIsVisible = this._viewportVisibility.intersects(widgetRectangle);

                // Prioritize those that are currently within the viewport.
                if (widgetIsVisible) {
                    // Pull them out of the overall list of images...
                    images.splice(i, 1);
                    // Keep track of the visible images...
                    imagesInViewport.push(imageWidget);

                    const intersectionArea = this._viewportVisibility.intersectionArea(widgetRectangle);

                    // If we have an image that takes up more than 50% of the viewport, find the biggest, so that we can load that "alone".
                    if (intersectionArea / viewportVisibilityArea >= 0.5 && intersectionArea > biggestIntersectionArea) {
                        heroImage = imageWidget;
                        biggestIntersectionArea = intersectionArea;
                    }
                }
            }

            return {
                heroImage,
                imagesInViewport,
                imagesOutsideViewport: images
            };
        },

        sceneViewerFromModel(model) {
            return this._imageToSceneMap.get(model);
        }

    });

    /**
     * Responsible for prioritizing load of image widgets within the initial visible viewport. This concept is
     * only applied to the initial scenes.
     *
     * @see  ExperienceViewer
     *
     * @class InitialScenesController
     */
    const InitialScenesController = Marionette.Controller.extend("InitialScenesController", {

        /**
         * Constructor.
         *
         * @param  {ExperienceViewModel} experienceViewModel The current <tt>ExperienceViewModel</tt>.
         */
        constructor(experienceViewModel) {
            this.__super();

            this._strategy = new ImageWidgetProcessStrategy(experienceViewModel);
        },

        /**
         * Processes the specified scenes' widgets, prioritizing image widgets that are
         * currently in the visible viewport. All other widgets are immediately added.
         *
         * The provided <tt>SceneViewer</tt>'s, must have <tt>extractCollection</tt> set to
         * false, otherwise they will render all widgets immediately.
         *
         * @param  {...SceneViewer} scenes The <tt>SceneViewer</tt>'s to process widgets for.
         * @return {Promise}               A promise that resolves when the images contained in
         *                                 the visible viewport has been loaded.
         */
        process(...args) {
            const executionStrategy = this._strategy.execute(args);

            const heroPromise = executionStrategy.heroImage ?
                this._loadImage(executionStrategy.heroImage) :
                Promise.resolved();

            // First, load the hero image, if any...
            return heroPromise
                // Load the rest of the images in viewport in parallel. Returning the combined promise.
                .then(() => this._loadImage(executionStrategy.imagesInViewport))
                .then(() => {
                    // Add the rest of the images.
                    this._loadImage(executionStrategy.imagesOutsideViewport);
                });
        },

        /**
         * Adds the image widget (or array of image widgets) to their respective scene
         * viewer, returning a <tt>Promise</tt> that resolves once the images has loaded.
         *
         * @param  {ImageWidgetModel|ImageWidgetMode[]} The <tt>ImageWidgetModel</tt> (or array of <tt>ImageWidgetModel</tt>'s) to "load".
         * @return {Promise}                            A <tt>Promise</tt> that resolves once the images has loaded.
         */
        _loadImage(imageWidgetModel) {
            const imageModels = _.isArray(imageWidgetModel) ? imageWidgetModel : [imageWidgetModel];
            const promises = _.map(imageModels, (model) => {
                const sceneViewer = this._strategy.sceneViewerFromModel(model);
                sceneViewer.collection.add(model);

                const widgetView = sceneViewer.widgetViewFromModel(model);

                return Promise.deferred((deferred) => {
                    // Proceed even on image load failure - hence 'always'.
                    widgetView.promise().always(deferred.resolve);
                });
            });

            return Promise.all(promises);
        }
    });

    return {

        /**
         * Static convenience method delegating to the instance's <tt>process</tt> function.
         *
         * @param  {ExperienceViewModel} experienceViewModel The current <tt>ExperienceViewModel</tt>.
         * @param  {...SceneViewer} experienceViewModel The <tt>SceneViewer</tt>'s to process widgets for.
         *
         * @return {Promise}               A promise that resolves when ALL images belonging to
         *                                 the specified scenes have been loaded.
         *
         * @see  InitialScenesController#process
         */
        process(experienceViewModel, ...args) {
            const controller = new InitialScenesController(experienceViewModel);
            return controller.process(...args);
        },

        ImageWidgetProcessStrategy

    };
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/InitialScenesController.js